import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  EXECUTION_TEMPLATE,
  EXECUTION_STATUS_LABELS,
  type ExecutionStatus,
} from "@/lib/basProcess/constants";
import { Plus, Trash2, ListChecks } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";

interface Props {
  projectId: string;
}

interface Item {
  id: string;
  area: string | null;
  title: string;
  status: ExecutionStatus;
  responsible: string | null;
  checked_at: string | null;
  comment: string | null;
  sort_order: number;
}

export const ExecutionFollowup: React.FC<Props> = ({ projectId }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newArea, setNewArea] = useState("");

  const load = async () => {
    const { data, error } = await supabase
      .from("execution_followup_items")
      .select("id, area, title, status, responsible, checked_at, comment, sort_order")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true });
    if (error) toast.error("Kunde inte läsa uppföljningen");
    else setItems((data || []) as Item[]);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const seed = async () => {
    setSeeding(true);
    const rows = EXECUTION_TEMPLATE.map((t, i) => ({
      project_id: projectId,
      area: t.area,
      title: t.title,
      description: t.description ?? null,
      source: "template",
      sort_order: i,
    }));
    const { error } = await supabase.from("execution_followup_items").insert(rows);
    if (error) toast.error("Kunde inte skapa kontrollpunkter");
    else {
      toast.success("Standardkontrollpunkter skapade");
      await load();
    }
    setSeeding(false);
  };

  const update = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    const { error } = await supabase.from("execution_followup_items").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    await supabase.from("execution_followup_items").delete().eq("id", id);
  };

  const add = async () => {
    if (!newTitle.trim()) return;
    const { error } = await supabase.from("execution_followup_items").insert({
      project_id: projectId,
      title: newTitle.trim(),
      area: newArea.trim() || null,
      source: "manual",
      sort_order: items.length,
    });
    if (error) return toast.error("Kunde inte lägga till");
    setNewTitle("");
    setNewArea("");
    await load();
  };

  const relevant = items.filter((i) => i.status !== "na");
  const checked = relevant.filter((i) => i.status !== "not_checked").length;
  const progress = relevant.length ? Math.round((checked / relevant.length) * 100) : 0;
  const deviations = items.filter((i) => i.status === "deviation").length;

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar uppföljning...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="21"
        title="Uppföljning av utförandet"
        description="Kontroll på plats att projekterade åtgärder faktiskt genomförs (Bas-U)."
        icon={ListChecks}
        meta={
          <div className="flex items-center gap-4">
            {deviations > 0 && <Badge variant="destructive">{deviations} avvikelser</Badge>}
            <div className="w-40">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Kontrollerat</span><span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>
        }
      />
      <Card>
        <CardContent className="pt-6">
          {items.length === 0 ? (
            <div className="text-center py-10 space-y-3">
              <ListChecks className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Inga kontrollpunkter ännu.</p>
              <Button onClick={seed} disabled={seeding}>
                {seeding ? "Skapar..." : "Skapa standardkontrollpunkter"}
              </Button>
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={it.id} className="py-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {it.area && <Badge variant="outline" className="text-[10px] mr-2">{it.area}</Badge>}
                      <span className="font-medium">{it.title}</span>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => remove(it.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(EXECUTION_STATUS_LABELS) as ExecutionStatus[]).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={it.status === s ? (s === "deviation" ? "destructive" : "default") : "outline"}
                        onClick={() =>
                          update(it.id, {
                            status: s,
                            checked_at: s === "not_checked" ? null : new Date().toISOString().slice(0, 10),
                          })
                        }
                      >
                        {EXECUTION_STATUS_LABELS[s]}
                      </Button>
                    ))}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Input
                      placeholder="Ansvarig"
                      defaultValue={it.responsible ?? ""}
                      onBlur={(e) => update(it.id, { responsible: e.target.value })}
                    />
                    <Textarea
                      className="sm:col-span-2"
                      rows={1}
                      placeholder="Kommentar / åtgärd"
                      defaultValue={it.comment ?? ""}
                      onBlur={(e) => update(it.id, { comment: e.target.value })}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-2 sm:grid-cols-[160px_1fr_auto]">
            <Input placeholder="Område" value={newArea} onChange={(e) => setNewArea(e.target.value)} />
            <Input
              placeholder="Ny kontrollpunkt"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <Button onClick={add}><Plus className="h-4 w-4 mr-1" />Lägg till</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExecutionFollowup;
